import { useEffect } from "react";
import { parseCookies } from "nookies";
import { useRouter } from "next/router";
import { getDataLayer } from "utils/analytics";
import { OPTIMIZE_EXPERIMENTS, OPTIMIZE_BUCKETS } from "utils/abTestConfig";

/**
 * Return a human readable variant name of the the test that the user is currently in.
 *
 * @returns {string} variant name
 */
export const useActiveTestGroup = (): string | null => {
  // In most cases, we'll read the bucket value off a cookie.
  const { atltestbucketv1: bucket } = parseCookies();

  // For debugging, we can use an ?optimize_experiment URL param.
  const { query: params } = useRouter();
  // URL params override the cookie definition, otherwise, return null.
  // Why null? Honestly, for testing purposes.
  return (
    (params["optimize_experiment"] as string) ||
    OPTIMIZE_BUCKETS[parseInt(bucket)] ||
    null
  );
};

/**
 * Activate an AB test by pushing information about the experiment and variants
 * into GTM.
 *
 * @param {string} name slug of the experiment name
 */
export const useActivateAbTest = (name: string) => {
  const activeTestGroup = useActiveTestGroup();
  useEffect(() => {
    const normalizedTestGroup =
      activeTestGroup === "control" ? `${name}.control` : activeTestGroup;

    // Split the test group into test name and test variant.
    const [testName, testVariant] = (normalizedTestGroup || "").split(".");
    const experimentDefinition = OPTIMIZE_EXPERIMENTS[testName];
    // If the test isn't in the data structure or the test name does not match
    // the one we're trying to activate, bail out.
    if (!experimentDefinition || testName !== name) return;
    // Otherwise, push variables to activate the test into the dataLayer.
    getDataLayer().push({
      event: "Optimize Test",
      abTest: {
        experiment_id: experimentDefinition.id,
        experiment_variant: experimentDefinition.groups.indexOf(testVariant),
        abt_experiment: normalizedTestGroup,
        abt_value: parseCookies()["atltestbucketv1"],
      },
    });
  }, [activeTestGroup, name]);
};
